import type { DefaultOptions } from '@tanstack/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/providers/ThemeProvider';
import App from './App';
import './index.css';

function isChunkLoadError(error: unknown): boolean {
  const message =
    typeof error === 'string' ? error : (error as Error)?.message || String(error || '');
  const messageLower = message.toLowerCase();

  return (
    messageLower.includes('failed to fetch dynamically imported module') ||
    messageLower.includes('error loading dynamically imported module') ||
    messageLower.includes('importing a module script failed') ||
    messageLower.includes('loading chunk') ||
    messageLower.includes('failed to load module script') ||
    messageLower.includes('expected a javascript-or-wasm module script') ||
    messageLower.includes('mime type') ||
    messageLower.includes("reading 'default'") ||
    messageLower.includes('reading default') ||
    messageLower.includes("property 'default'")
  );
}

function reloadOnceForFreshChunks() {
  const hasReloaded = sessionStorage.getItem('haulercentral_chunk_reload_retry');
  if (hasReloaded) return;

  sessionStorage.setItem('haulercentral_chunk_reload_retry', 'true');
  window.location.reload();
}

function setupChunkLoadErrorHandler() {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    console.warn('[HaulerCentral] Dynamic import preload failed. Reloading for fresh assets.');
    reloadOnceForFreshChunks();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      console.warn(
        '[HaulerCentral] Dynamic import failed. Reloading for fresh assets.',
        event.reason
      );
      reloadOnceForFreshChunks();
    }
  });

  window.addEventListener('load', () => {
    setTimeout(() => {
      sessionStorage.removeItem('haulercentral_chunk_reload_retry');
    }, 3000);
  });
}

setupChunkLoadErrorHandler();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: (failureCount: number, error: any) => {
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: false,
    },
    mutations: {
      retry: 1,
      onError: (error: any) => {
        console.error('[Mutation Error]:', error?.message || error);
      },
    },
  } satisfies DefaultOptions,
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find root element');
createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/">
        <ThemeProvider defaultTheme="light">
          <App />
          <Toaster />
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
